import type { CSSProperties } from "react";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { IzanagiMark } from "./IzanagiMark";
import { IzanagiWordmark } from "./IzanagiWordmark";

interface AppBrandProps {
	style?: CSSProperties;
}

const LOGO_HEIGHT = 18;

/** サイドバーの 1 行 (ナビ項目・折りたたみ時のマークボタン) の高さ。 */
const SIDEBAR_ROW_HEIGHT = 32;

/**
 * 1 行目を上に空ける量。行高ではなく余白で押し下げることで、マークの位置を折りたたみの
 * 前後で揃えつつ、1 行目と 2 行目が行ボックスの余白ぶん離れて見えるのを防ぐ。
 */
const FIRST_LINE_OFFSET = (SIDEBAR_ROW_HEIGHT - LOGO_HEIGHT) / 2;

/** 2 行目 (組織名) の行の高さ。ブランド表示の積み上げ寸法であって本文の行送りではない。 */
const ORGANIZATION_LINE_HEIGHT = 14;

/** ナビ項目のアイコンの幅。マークをこの幅の枠に中央寄せし、ナビのアイコン・ラベルと位置を揃える。 */
const NAV_ICON_SIZE = 18;

const ORGANIZATION_STYLE: CSSProperties = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	fontSize: "var(--text-caption)",
	fontWeight: 500,
	lineHeight: `${ORGANIZATION_LINE_HEIGHT}px`,
	color: "var(--fg-3)",
};

/**
 * サイドバー最上部のブランド表示。1 行目にロゴ、2 行目に組織名を積み、
 * 長い組織名でもサイドバーの幅を使い切れるようにする。
 * チャットへの遷移はナビのチャット項目が担う。
 */
export function AppBrand({ style }: AppBrandProps) {
	const organizationName = useCurrentUserStore((s) => s.organizationName);

	const containerStyle: CSSProperties = {
		display: "flex",
		alignItems: "flex-start",
		gap: 10,
		minWidth: 0,
		flexShrink: 0,
		padding: `${FIRST_LINE_OFFSET}px 8px 0`,
		color: "var(--fg-1)",
		...style,
	};

	return (
		<div data-testid="app-brand" style={containerStyle}>
			<span
				style={{
					display: "flex",
					justifyContent: "center",
					width: NAV_ICON_SIZE,
					height: LOGO_HEIGHT,
					flexShrink: 0,
				}}
			>
				<IzanagiMark height={LOGO_HEIGHT} />
			</span>
			<span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
				<IzanagiWordmark height={LOGO_HEIGHT} />
				{organizationName && (
					<span
						data-testid="app-brand-organization"
						title={organizationName}
						data-tooltip-if-truncated
						style={ORGANIZATION_STYLE}
					>
						{organizationName}
					</span>
				)}
			</span>
		</div>
	);
}
